import React, { useEffect, useMemo, useRef } from 'react';
import {
  ReviewHistoryTarget,
  ReviewRevision
} from '../types/bookReview';
import { Character, Page } from '../types/storybook';
import ReviewPagePreview from './ReviewPagePreview';
import './BookReviewDashboard.css';

export interface RevisionCompareDialogProps {
  target: ReviewHistoryTarget;
  canvasSize: string;
  currentPage?: Page;
  currentCharacter?: Character;
  revisions: ReviewRevision[];
  selectedRevisionId?: string;
  loading?: boolean;
  restoring?: boolean;
  error?: string | null;
  resolveImageUrl?: (url: string) => string;
  onSelectRevision: (revisionId: string) => void;
  onRestore: (revisionId: string) => void | Promise<void>;
  onClose: () => void;
}

const formatRevisionDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const characterImage = (character?: Character) => (
  character?.thumbnailUrl || character?.imageUrl
);

const RevisionCompareDialog: React.FC<RevisionCompareDialogProps> = ({
  target,
  canvasSize,
  currentPage,
  currentCharacter,
  revisions,
  selectedRevisionId,
  loading = false,
  restoring = false,
  error,
  resolveImageUrl = value => value,
  onSelectRevision,
  onRestore,
  onClose
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const selectedRevision = useMemo(
    () => revisions.find(revision => revision.id === selectedRevisionId) || revisions[0],
    [revisions, selectedRevisionId]
  );

  useEffect(() => {
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const historicalCharacter = selectedRevision?.characterSnapshot;
  const historicalCharacterImage = characterImage(historicalCharacter);
  const currentCharacterImage = characterImage(currentCharacter);

  return (
    <div
      className="book-review-dialog-backdrop"
      role="presentation"
      data-testid="revision-compare-backdrop"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="book-review-revision-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-review-revision-title"
        aria-describedby="book-review-revision-help"
        data-testid="revision-compare-dialog"
      >
        <header className="book-review-revision-header">
          <div>
            <p className="book-review-eyebrow">Immutable history</p>
            <h2 id="book-review-revision-title">Compare {target.label}</h2>
            <p id="book-review-revision-help">
              Restoring keeps this history and creates a new revision; it never overwrites an older version.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="book-review-icon-button"
            aria-label="Close revision comparison"
            data-testid="revision-compare-close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        {loading ? (
          <div className="book-review-dialog-state" role="status">Loading version history…</div>
        ) : error ? (
          <div className="book-review-dialog-state error" role="alert">{error}</div>
        ) : revisions.length === 0 ? (
          <div className="book-review-dialog-state">No earlier revisions are available yet.</div>
        ) : (
          <>
            <div className="book-review-revision-picker">
              <label htmlFor="book-review-revision-select">Earlier version</label>
              <select
                id="book-review-revision-select"
                data-testid="revision-compare-select"
                value={selectedRevision?.id || ''}
                onChange={event => onSelectRevision(event.target.value)}
              >
                {revisions.map(revision => (
                  <option key={revision.id} value={revision.id}>
                    Revision {revision.revisionNumber} · {formatRevisionDate(revision.createdAt)} · {revision.reason}
                  </option>
                ))}
              </select>
            </div>

            {selectedRevision && (
              <div className="book-review-compare-grid" data-testid="revision-compare-grid">
                <article>
                  <header>
                    <span className="book-review-current-pill">Current</span>
                    <strong>{target.label}</strong>
                  </header>
                  {target.type === 'page' && currentPage && (
                    <ReviewPagePreview
                      page={currentPage}
                      canvasSize={canvasSize}
                      ariaLabel={`Current ${target.label}`}
                      testId="revision-current-page"
                      resolveImageUrl={resolveImageUrl}
                    />
                  )}
                  {target.type === 'character' && (
                    <div className="book-review-character-compare">
                      {currentCharacterImage ? (
                        <img src={resolveImageUrl(currentCharacterImage)} alt={`Current ${target.label}`} />
                      ) : (
                        <span>No current reference image</span>
                      )}
                      <p>{currentCharacter?.prompt || 'No current character description.'}</p>
                    </div>
                  )}
                </article>

                <article>
                  <header>
                    <span className="book-review-revision-pill">
                      Revision {selectedRevision.revisionNumber}
                    </span>
                    <strong>{formatRevisionDate(selectedRevision.createdAt)}</strong>
                  </header>
                  {target.type === 'page' && selectedRevision.pageSnapshot && (
                    <ReviewPagePreview
                      page={selectedRevision.pageSnapshot}
                      canvasSize={canvasSize}
                      ariaLabel={`Revision ${selectedRevision.revisionNumber} of ${target.label}`}
                      testId="revision-historical-page"
                      resolveImageUrl={resolveImageUrl}
                    />
                  )}
                  {target.type === 'page' && !selectedRevision.pageSnapshot && (
                    <div className="book-review-missing-preview">This revision has no visual snapshot.</div>
                  )}
                  {target.type === 'character' && (
                    <div className="book-review-character-compare">
                      {historicalCharacterImage ? (
                        <img
                          src={resolveImageUrl(historicalCharacterImage)}
                          alt={`Revision ${selectedRevision.revisionNumber} of ${target.label}`}
                        />
                      ) : (
                        <span>This revision has no reference image.</span>
                      )}
                      <p>{historicalCharacter?.prompt || 'No saved character description.'}</p>
                    </div>
                  )}
                </article>
              </div>
            )}

            {selectedRevision && (
              <div className="book-review-revision-details">
                <div>
                  <strong>Saved because</strong>
                  <span>{selectedRevision.reason}</span>
                </div>
                <div>
                  <strong>Changed layers</strong>
                  <span>
                    {selectedRevision.changedLayers?.length
                      ? selectedRevision.changedLayers.join(', ')
                      : 'Snapshot only'}
                  </span>
                </div>
                {selectedRevision.summary && (
                  <div>
                    <strong>Notes</strong>
                    <span>{selectedRevision.summary}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <footer className="book-review-revision-actions">
          <button type="button" className="book-review-button secondary" onClick={onClose}>
            Keep current
          </button>
          <button
            type="button"
            className="book-review-button primary"
            data-testid="revision-restore"
            disabled={!selectedRevision || loading || restoring}
            onClick={() => selectedRevision && onRestore(selectedRevision.id)}
          >
            {restoring ? 'Restoring…' : 'Restore as new revision'}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default RevisionCompareDialog;
